import { Link, useLocation } from 'react-router-dom';

import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import NotificationBell from '@/components/notifications/NotificationBell';
import { ControlPanelTrigger } from '@/components/layout/control-panel/AppControlPanel';
import ThemeToggle from '@/components/ThemeToggle';

/**
 * Persistent compact top bar for mobile and tablet — provides the brand
 * mark, notifications and the account menu on every screen below the `lg`
 * breakpoint (1024px). The desktop Navbar stays `hidden lg:block`; this bar
 * plus the BottomNav form the touch-friendly app shell for phones AND
 * tablets (where the dense desktop navbar otherwise gets cramped).
 */
const MobileTopBar = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { t } = useI18n();

  // Admin shell renders its own focused chrome.
  if (pathname.startsWith('/admin')) return null;

  return (
    <header
      className="lg:hidden sticky top-0 z-40 bg-background/85 backdrop-blur-2xl border-b border-border/30 pt-safe px-safe"
      role="banner"
    >
      <div className="h-14 px-3 sm:px-5 max-w-3xl mx-auto flex items-center justify-between gap-2">
        <Link
          to="/"
          className="flex items-center min-w-[44px] min-h-[44px] -ml-1 px-1 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
          aria-label="Scuvio"
        >
          <img src={'/scuvio-logo-light.svg'} alt="Scuvio" className="h-7 w-auto hidden dark:block" />
          <img src={'/scuvio-logo-dark.svg'} alt="Scuvio" className="h-7 w-auto dark:hidden" />
        </Link>

        <div className="flex items-center gap-0.5">
          <ThemeToggle />
          {user ? (
            <>
              <NotificationBell />
              <ControlPanelTrigger />
            </>
          ) : (
            <Button
              size="sm"
              asChild
              className="rounded-xl h-11 min-w-[44px] px-4 text-xs font-medium"
            >
              <Link to="/auth" state={{ from: pathname, mode: 'login' }}>
                {t('auth.login')}
              </Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};

export default MobileTopBar;